import { Box, Text, VStack, Separator, Icon } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { t } from "i18next";
import {
  CraAttribute,
  CraAttributes,
  CraAttributeType,
  getOptionTranslation,
} from "@keita/lanepatrol-sdk";
import { CraWaySnapshot } from "../../../../../coding/models/CraWaySnapshot";
import { CraWaySnapshotApi } from "../../../../../coding/services/CraWaySnapshotApi";
import { LoadingScreen } from "../../../../../shared/views/LoadingScreen/LoadingScreen";
import { CraAttributeUtils } from "../../../../../coding/utils/attribute-utils";
import { ArrowForwardIcon } from "../../../../../shared/views/icons";

interface Change {
  indexes: number[];
  fromValue?: string;
  toValue?: string;
}
interface ChangeRepository {
  attributeDefinition: CraAttribute;
  changes: Change[];
}

interface Props {
  snapshot?: CraWaySnapshot;
}
const CraWaySnapshotPreviewBase: React.FC<Props> = ({ snapshot }) => {
  const [data, setData] = useState<Map<string, ChangeRepository>>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (snapshot) {
      setLoading(true);
      CraWaySnapshotApi.getPreview(snapshot.id).then((response) => {
        const newData = new Map<string, ChangeRepository>();

        for (const operation of response.data) {
          for (const change of operation.changes) {
            var repository = newData.get(change.fullName) || {
              attributeDefinition: CraAttributes.getByName(change.fullName)!,
              changes: [],
            };
            const prevChange = repository.changes.find(
              (item) =>
                item.toValue === change.toValue &&
                item.fromValue === change.fromValue,
            );
            if (prevChange) {
              prevChange.indexes.push(operation.craNodeIndex);
            } else {
              repository.changes.push({
                indexes: [operation.craNodeIndex],
                fromValue: change.fromValue,
                toValue: change.toValue,
              });
            }
            newData.set(change.fullName, repository);
          }
        }

        setData(newData);

        setLoading(false);
      });
    } else {
      setData(undefined);
    }

    // eslint-disable-next-line
  }, [snapshot]);

  return (
    <Box
      position="relative"
      flexGrow={1}
      flexShrink={0}
      width="50%"
      overflow="auto"
    >
      {data ? (
        <VStack py={4} alignItems="start" separator={<Separator />} gap={4}>
          {[...data.values()].map(({ attributeDefinition, changes }, i) => {
            return (
              <Box key={i} px={4}>
                <Text fontSize="sm" fontWeight="semibold" pb={2}>
                  {CraAttributeUtils.getLabel(attributeDefinition.name)}
                </Text>
                <VStack alignItems="start">
                  {changes.map((change, i) => {
                    return (
                      <Box key={i} fontSize="sm">
                        <Text>
                          {`${t("coding:history.nodes")} ${compileNodeIndexes(
                            change.indexes,
                          )}`}
                        </Text>
                        <Box>
                          <Box as="span" bg="red.100" borderRadius={4}>
                            {getReadableValue(
                              change.fromValue,
                              attributeDefinition,
                            )}
                          </Box>
                          <Icon as={ArrowForwardIcon} mx={1} />
                          <Box as="span" bg="green.100" borderRadius={4}>
                            {getReadableValue(
                              change.toValue,
                              attributeDefinition,
                            )}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </VStack>
              </Box>
            );
          })}
          {!data.size && (
            <Box fontSize="sm" px={4} py={2}>
              {t("coding:history.noChanges")}
            </Box>
          )}
        </VStack>
      ) : (
        <Text fontSize="sm" px={4} py={2}>
          {t("coding:history.selectPreview")}
        </Text>
      )}

      {loading && <LoadingScreen />}
    </Box>
  );
};

export const CraWaySnapshotPreview = observer(CraWaySnapshotPreviewBase);

const getReadableValue = (value: any, attrBox: CraAttribute) => {
  var printedValue = value;
  if (value && attrBox.type === CraAttributeType.Select) {
    printedValue = t(getOptionTranslation(value));
  }

  return printedValue || "-";
};

// this is only this simple because the indexes are naturally sorted
const compileNodeIndexes = (indexes: number[]) => {
  console.log(indexes);

  var text: string[] = [];
  var sequenceStart: number | undefined;
  var latestNumber: number | undefined;

  const logSequence = () =>
    text.push(
      sequenceStart === latestNumber
        ? `${sequenceStart! + 1}`
        : `${sequenceStart! + 1}-${latestNumber! + 1}`,
    );

  const startSequence = (n: number) => {
    sequenceStart = n;
    latestNumber = n;
  };

  for (const n of indexes) {
    if (latestNumber === undefined) {
      startSequence(n);
      continue;
    }

    if (n === latestNumber + 1) {
      latestNumber = n;
    } else {
      logSequence();
      startSequence(n);
    }
  }
  logSequence();

  return text.join(", ");
};
